// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import {
  Square,
  Circle,
  Type,
  MapPin,
  Pentagon,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  RotateCw,
  Layers,
  Settings,
  List,
  Plus,
  Minus,
  AlignRight,
  Maximize2,
  X,
  FileImage,
  Map,
  Building,
  Trash2,
  Save,
} from "lucide-react";
import {
  LAYER_CONFIG,
  LAYERS,
  PARK_OBJECTS,
} from "../../utils/scenarioEditorUtils";
import SpaceUseCaseConfig from "./SpaceUseCaseConfig";

export default function RightPanel({
  rightPanel,
  setRightPanel,
  showRightPanel,
  isRightPanelFloating,
  isRightPanelMinimized,
  rightPanelPosition,
  handleRightPanelMouseDown,
  toggleRightPanelMinimize,
  dockRightPanel,
  floatRightPanel,
  toggleRightPanel,
  selectedElement,
  updateElement,
  rotateElement,
  toggleElementLock,
  toggleElementVisibility,
  elements,
  selectedElements,
  setSelectedElements,
  currentLayer,
  setCurrentLayer,
  layerVisibility,
  toggleLayerVisibility,
  getElementsForLayer,
  backgroundImage,
  backgroundImageURL,
  setBackgroundImageURL,
  importBackgroundFromURL,
  importBackgroundImage,
  backgroundInputRef,
  setBackgroundImage,
  scenarioMetadata,
  referencePoints,
  toggleReferencePoint,
  updatePinGPS,
  onSaveAll,
  isSaving,
  canSave,
  onDeleteSpace,
  isDeleting,
  useCases,
}) {
  if (!showRightPanel) return null;

  return (
    <div
      className={`bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg ${
        isRightPanelFloating
          ? "fixed z-50 rounded-lg"
          : "min-w-[240px] max-w-[400px] w-[20vw] flex-shrink-0 border-l border-t-0 border-b-0 border-r-0"
      }`}
      style={
        isRightPanelFloating
          ? {
              left: `${Math.min(rightPanelPosition.x, window.innerWidth - 320)}px`,
              top: `${Math.min(rightPanelPosition.y, window.innerHeight - 100)}px`,
              width: `${Math.min(320, window.innerWidth * 0.3)}px`,
              maxWidth: "90vw",
              maxHeight: isRightPanelMinimized ? "auto" : "70vh",
            }
          : {}
      }
      onMouseDown={isRightPanelFloating ? handleRightPanelMouseDown : undefined}
    >
      <div
        className={`${
          isRightPanelFloating ? "right-panel-drag-handle cursor-move" : ""
        } flex items-center justify-between px-3 py-2 border-b border-gray-300 dark:border-slate-700 dark:bg-slate-700/50`}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wide">
            {isRightPanelFloating ? "Floating Panel" : "Properties"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isRightPanelFloating && (
            <>
              <button
                onClick={toggleRightPanelMinimize}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
                title={isRightPanelMinimized ? "Expand" : "Minimize"}
              >
                {isRightPanelMinimized ? (
                  <Plus className="w-3 h-3" />
                ) : (
                  <Minus className="w-3 h-3" />
                )}
              </button>
              <button
                onClick={dockRightPanel}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
                title="Dock to Right"
              >
                <AlignRight className="w-3 h-3" />
              </button>
            </>
          )}
          {!isRightPanelFloating && (
            <button
              onClick={floatRightPanel}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Float Panel"
            >
              <Maximize2 className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={toggleRightPanel}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
            title="Close Panel"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {!isRightPanelMinimized && (
        <div className="flex flex-col h-[calc(100vh-80px)] overflow-hidden">
          <div className="flex border-b border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 flex-shrink-0">
            <button
              onClick={() => setRightPanel("layers")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightPanel === "layers"
                  ? "text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-800 border-b-2 border-gray-900 dark:border-slate-200"
                  : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700/50"
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <Layers className="w-3.5 h-3.5" />
                <span>Layers</span>
              </div>
            </button>
            <button
              onClick={() => setRightPanel("objects")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightPanel === "objects"
                  ? "text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-800 border-b-2 border-gray-900 dark:border-slate-200"
                  : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700/50"
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <List className="w-3.5 h-3.5" />
                <span>Objects</span>
              </div>
            </button>
            <button
              onClick={() => setRightPanel("properties")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
                rightPanel === "properties"
                  ? "text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-800 border-b-2 border-gray-900 dark:border-slate-200"
                  : "text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700/50"
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <Settings className="w-3.5 h-3.5" />
                <span>Properties</span>
              </div>
            </button>
          </div>
          <div
            className={`p-4 overflow-y-auto flex-1 min-h-0 ${isRightPanelFloating ? "max-h-96" : ""}`}
          >
            {/* Properties Panel */}
            {rightPanel === "properties" && selectedElement && (
              <PropertiesPanel
                selectedElement={selectedElement}
                updateElement={updateElement}
                rotateElement={rotateElement}
                toggleElementLock={toggleElementLock}
                toggleElementVisibility={toggleElementVisibility}
                referencePoints={referencePoints}
                toggleReferencePoint={toggleReferencePoint}
                updatePinGPS={updatePinGPS}
                scenarioMetadata={scenarioMetadata}
                useCases={useCases}
              />
            )}

            {/* Objects Panel */}
            {rightPanel === "objects" && (
              <ObjectsPanel
                elements={elements}
                selectedElements={selectedElements}
                setSelectedElements={setSelectedElements}
                setRightPanel={setRightPanel}
                toggleElementVisibility={toggleElementVisibility}
                toggleElementLock={toggleElementLock}
              />
            )}

            {/* Layers Panel */}
            {rightPanel === "layers" && (
              <LayersPanel
                currentLayer={currentLayer}
                setCurrentLayer={setCurrentLayer}
                layerVisibility={layerVisibility}
                toggleLayerVisibility={toggleLayerVisibility}
                getElementsForLayer={getElementsForLayer}
                backgroundImage={backgroundImage}
                backgroundImageURL={backgroundImageURL}
                setBackgroundImageURL={setBackgroundImageURL}
                importBackgroundFromURL={importBackgroundFromURL}
                importBackgroundImage={importBackgroundImage}
                backgroundInputRef={backgroundInputRef}
                setBackgroundImage={setBackgroundImage}
                scenarioMetadata={scenarioMetadata}
              />
            )}
          </div>
          {onSaveAll && (
            <div className="p-3 border-t border-gray-200 dark:border-slate-700 flex-shrink-0 space-y-2">
              <button
                type="button"
                onClick={onSaveAll}
                disabled={!canSave || isSaving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-color text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Speichert…" : "Szenario speichern"}
              </button>
              {selectedElement && onDeleteSpace && (
                <button
                  type="button"
                  onClick={() => onDeleteSpace(selectedElement.id)}
                  disabled={isDeleting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  {isDeleting ? "Löscht…" : "Fläche löschen"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Properties Panel Component
function PropertiesPanel({
  selectedElement,
  updateElement,
  rotateElement,
  toggleElementLock,
  toggleElementVisibility,
  referencePoints,
  toggleReferencePoint,
  updatePinGPS,
  scenarioMetadata,
  useCases,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
          Properties
        </h3>
        {/* Show the real DB id only once saved; the internal canvas element id
            isn't meaningful to users, so it's no longer displayed. */}
        {selectedElement.spaceId ? (
          <span
            className="text-[11px] text-gray-400 dark:text-slate-500 truncate max-w-[55%]"
            title={`ID: ${selectedElement.spaceId}`}
          >
            ID: {selectedElement.spaceId}
          </span>
        ) : (
          <span className="text-[11px] text-amber-600 dark:text-amber-400">
            Neu (noch nicht gespeichert)
          </span>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Layer
        </label>
        <select
          value={selectedElement.layer}
          onChange={(e) =>
            updateElement(selectedElement.id, {
              layer: e.target.value,
            })
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
        >
          {Object.entries(LAYER_CONFIG).map(([layerId, config]) => (
            <option key={layerId} value={layerId}>
              {config.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Label
        </label>
        <input
          type="text"
          value={selectedElement.label || selectedElement.text || ""}
          onChange={(e) => {
            const patch = {
              [selectedElement.type === "text" ? "text" : "label"]:
                e.target.value,
            };
            if (selectedElement.spaceId) {
              patch.name = e.target.value;
            }
            updateElement(selectedElement.id, patch);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
        />
      </div>

      {/* Flächen-Info — inline (replaces the old "Edit Info" modal). Saved to
          the DB on "Szenario speichern". */}
      <div className="pt-3 border-t border-gray-200 dark:border-slate-700 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
          Flächen-Info
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            SVG Code
          </label>
          <input
            type="text"
            maxLength={50}
            value={selectedElement.space_code || ""}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                space_code: e.target.value,
              })
            }
            placeholder={selectedElement.id}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Typ
          </label>
          <select
            value={selectedElement.space_type || "event_area"}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                space_type: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
          >
            <option value="event_area">event_area</option>
            <option value="parking_spot">parking_spot</option>
            <option value="delivery_zone">delivery_zone</option>
            <option value="radbox_slot">radbox_slot</option>
            <option value="charging_point">charging_point</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Kapazität
            </label>
            <input
              type="number"
              min="0"
              value={selectedElement.capacity ?? ""}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  capacity: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Einheit
            </label>
            <input
              type="text"
              maxLength={50}
              value={selectedElement.capacity_unit || "persons"}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  capacity_unit: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Beschreibung
          </label>
          <textarea
            rows={2}
            value={selectedElement.description || ""}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                description: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Ausstattung
          </label>
          <input
            type="text"
            value={
              Array.isArray(selectedElement.amenities)
                ? selectedElement.amenities.join(", ")
                : ""
            }
            onChange={(e) =>
              updateElement(selectedElement.id, {
                amenities: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="z. B. Strom, WLAN, WC"
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Wartung
          </label>
          <select
            value={selectedElement.maintenance_status || "operational"}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                maintenance_status: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
          >
            <option value="operational">operational</option>
            <option value="maintenance">maintenance</option>
            <option value="out_of_service">out_of_service</option>
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={selectedElement.is_active !== false}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  is_active: e.target.checked,
                })
              }
            />
            Aktiv
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={selectedElement.is_bookable !== false}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  is_bookable: e.target.checked,
                })
              }
            />
            Buchbar
          </label>
        </div>
        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            Nutzungsarten
          </label>
          <SpaceUseCaseConfig
            spaceId={selectedElement.spaceId}
            useCases={useCases}
          />
        </div>

        <div className="pt-2 border-t border-gray-200 dark:border-slate-700 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
            Erweitert
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Nummer
              </label>
              <input
                type="number"
                value={selectedElement.space_number ?? ""}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    space_number: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Höhe (m)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={selectedElement.height_m ?? ""}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    height_m: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                GPS Lat
              </label>
              <input
                type="number"
                step="any"
                value={selectedElement.gps_lat ?? ""}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    gps_lat: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                GPS Lng
              </label>
              <input
                type="number"
                step="any"
                value={selectedElement.gps_lng ?? ""}
                onChange={(e) =>
                  updateElement(selectedElement.id, {
                    gps_lng: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Zugangstyp
            </label>
            <select
              value={selectedElement.access_control_type || ""}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  access_control_type: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
            >
              <option value="">— kein —</option>
              <option value="barrier">barrier</option>
              <option value="bollard">bollard</option>
              <option value="lock">lock</option>
              <option value="none">none</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Geräte-ID
            </label>
            <input
              type="text"
              maxLength={255}
              value={selectedElement.access_control_device_id || ""}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  access_control_device_id: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            X
          </label>
          <input
            type="number"
            value={selectedElement.x || selectedElement.cx || 0}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                [selectedElement.cx !== undefined ? "cx" : "x"]:
                  parseFloat(e.target.value) || 0,
              })
            }
            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
            step="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Y
          </label>
          <input
            type="number"
            value={selectedElement.y || selectedElement.cy || 0}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                [selectedElement.cy !== undefined ? "cy" : "y"]:
                  parseFloat(e.target.value) || 0,
              })
            }
            className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
            step="1"
          />
        </div>
      </div>
      {selectedElement.type === "rect" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Width
            </label>
            <input
              type="number"
              value={selectedElement.width}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  width: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Height
            </label>
            <input
              type="number"
              value={selectedElement.height}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  height: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
            />
          </div>
        </div>
      )}
      {(selectedElement.type === "circle" ||
        selectedElement.type === "tree") && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Radius
          </label>
          <input
            type="number"
            value={selectedElement.r}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                r: parseFloat(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Rotation (°)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={selectedElement.rotation || 0}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                rotation: parseFloat(e.target.value) || 0,
              })
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            min="0"
            max="360"
            step="15"
          />
          <button
            onClick={() => rotateElement(selectedElement.id, 15)}
            className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded"
            title="Rotate 15°"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Fill Color
        </label>
        <input
          type="color"
          value={selectedElement.fill}
          onChange={(e) =>
            updateElement(selectedElement.id, {
              fill: e.target.value,
            })
          }
          className="w-full h-10 border border-gray-300 dark:border-slate-600 rounded-md"
        />
      </div>
      {selectedElement.type !== "text" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Stroke Color
            </label>
            <input
              type="color"
              value={selectedElement.stroke}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  stroke: e.target.value,
                })
              }
              className="w-full h-10 border border-gray-300 dark:border-slate-600 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Stroke Width
            </label>
            <input
              type="number"
              value={selectedElement.strokeWidth}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  strokeWidth: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
              min="0"
              step="0.5"
            />
          </div>
        </>
      )}
      {selectedElement.type === "text" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            Font Size
          </label>
          <input
            type="number"
            value={selectedElement.fontSize}
            onChange={(e) =>
              updateElement(selectedElement.id, {
                fontSize: parseFloat(e.target.value) || 12,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm dark:placeholder-slate-400"
            min="8"
            max="72"
          />
        </div>
      )}
      {selectedElement.type === "pin" && (
        <div className="space-y-4 border-t dark:border-slate-700 pt-4">
          <h4 className="font-medium text-gray-900 dark:text-slate-100">
            GPS Coordinates
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Latitude
              </label>
              <input
                type="number"
                step="0.000001"
                value={selectedElement.gps?.latitude || ""}
                onChange={(e) =>
                  updatePinGPS(
                    selectedElement.id,
                    e.target.value,
                    selectedElement.gps?.longitude || 0,
                  )
                }
                className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
                placeholder="e.g. 52.520008"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Longitude
              </label>
              <input
                type="number"
                step="0.000001"
                value={selectedElement.gps?.longitude || ""}
                onChange={(e) =>
                  updatePinGPS(
                    selectedElement.id,
                    selectedElement.gps?.latitude || 0,
                    e.target.value,
                  )
                }
                className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-sm dark:placeholder-slate-400"
                placeholder="e.g. 13.404954"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700 dark:text-slate-300">
              GPS Reference Point
            </span>
            <button
              onClick={() => toggleReferencePoint(selectedElement.id)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                referencePoints.includes(selectedElement.id)
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-200 border border-green-300 dark:border-green-700"
                  : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-slate-600"
              }`}
              disabled={!selectedElement.gps}
            >
              {referencePoints.includes(selectedElement.id)
                ? "Reference Point"
                : "Make Reference"}
            </button>
          </div>
          {referencePoints.length >= 2 && scenarioMetadata.scale && (
            <div className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded dark:border dark:border-green-800">
              GPS scaling active: {scenarioMetadata.scale.toFixed(2)} meters per
              pixel
            </div>
          )}
          {selectedElement.gps &&
            scenarioMetadata.coordinateSystem === "gps" && (
              <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded dark:border dark:border-blue-800">
                Calculated GPS: {selectedElement.gps.latitude.toFixed(6)},{" "}
                {selectedElement.gps.longitude.toFixed(6)}
              </div>
            )}
        </div>
      )}
      <div className="flex items-center gap-2 pt-4 border-t dark:border-slate-700">
        <button
          onClick={() => toggleElementLock(selectedElement.id)}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
            selectedElement.locked
              ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-200"
              : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200"
          }`}
        >
          {selectedElement.locked ? (
            <Lock className="w-4 h-4" />
          ) : (
            <Unlock className="w-4 h-4" />
          )}
          {selectedElement.locked ? "Locked" : "Unlocked"}
        </button>
        <button
          onClick={() => toggleElementVisibility(selectedElement.id)}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
            selectedElement.visible
              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200"
              : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200"
          }`}
        >
          {selectedElement.visible ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
          {selectedElement.visible ? "Visible" : "Hidden"}
        </button>
      </div>
    </div>
  );
}

// Objects Panel Component
function ObjectsPanel({
  elements,
  selectedElements,
  setSelectedElements,
  setRightPanel,
  toggleElementVisibility,
  toggleElementLock,
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
        Objects ({elements.length})
      </h3>
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {elements.map((element, index) => {
          const isSel = selectedElements.find((el) => el.id === element.id);
          let Icon;
          if (element.type === "rect") Icon = Square;
          else if (element.type === "circle") Icon = Circle;
          else if (element.type === "text") Icon = Type;
          else if (element.type === "pin") Icon = MapPin;
          else if (element.type === "polygon") Icon = Pentagon;
          else if (PARK_OBJECTS[element.type])
            Icon = PARK_OBJECTS[element.type].icon;
          else Icon = Square;
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                isSel
                  ? "bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600"
                  : "hover:bg-gray-50 dark:hover:bg-slate-700/50"
              }`}
              onClick={() => {
                setSelectedElements([element]);
                setRightPanel("properties");
              }}
            >
              <Icon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                  {element.label ||
                    element.text ||
                    `${element.type}-${index + 1}`}
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-400">
                  {LAYER_CONFIG[element.layer]?.name} •{" "}
                  {PARK_OBJECTS[element.type]?.label || element.type}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleElementVisibility(element.id);
                  }}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  {element.visible ? (
                    <Eye className="w-3 h-3 text-gray-500 dark:text-slate-400" />
                  ) : (
                    <EyeOff className="w-3 h-3 text-gray-400 dark:text-slate-500" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleElementLock(element.id);
                  }}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  {element.locked ? (
                    <Lock className="w-3 h-3 text-orange-500 dark:text-orange-400" />
                  ) : (
                    <Unlock className="w-3 h-3 text-gray-500 dark:text-slate-400" />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Layers Panel Component
function LayersPanel({
  currentLayer,
  setCurrentLayer,
  layerVisibility,
  toggleLayerVisibility,
  getElementsForLayer,
  backgroundImage,
  backgroundImageURL,
  setBackgroundImageURL,
  importBackgroundFromURL,
  importBackgroundImage,
  backgroundInputRef,
  setBackgroundImage,
  scenarioMetadata,
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
        Layers
      </h3>
      <div className="space-y-2">
        {Object.entries(LAYER_CONFIG)
          .reverse()
          .map(([layerId, config]) => {
            const layerElements = getElementsForLayer(layerId);
            const isActive = currentLayer === layerId;
            const isVisible = layerVisibility[layerId];
            return (
              <div
                key={layerId}
                className={`p-3 border rounded-lg transition-colors ${
                  isActive
                    ? "border-blue-300 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-200 dark:border-slate-600"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setCurrentLayer(layerId)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <config.icon className="w-4 h-4" />
                    <span className="font-medium text-sm dark:text-slate-200">
                      {config.name}
                    </span>
                  </button>
                  <button
                    onClick={() => toggleLayerVisibility(layerId)}
                    className={`p-1 rounded transition-colors ${
                      isVisible
                        ? "text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600"
                        : "text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-600"
                    }`}
                  >
                    {isVisible ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                  {config.description}
                </p>
                <p className="text-xs text-gray-400 dark:text-slate-500">
                  {layerElements.length} elements
                </p>
                {layerId === LAYERS.BACKGROUND && (
                  <BackgroundLayerControls
                    backgroundImage={backgroundImage}
                    backgroundImageURL={backgroundImageURL}
                    setBackgroundImageURL={setBackgroundImageURL}
                    importBackgroundFromURL={importBackgroundFromURL}
                    importBackgroundImage={importBackgroundImage}
                    backgroundInputRef={backgroundInputRef}
                    setBackgroundImage={setBackgroundImage}
                    layerVisibility={layerVisibility}
                    scenarioMetadata={scenarioMetadata}
                  />
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

// Background Layer Controls Component
function BackgroundLayerControls({
  backgroundImage,
  backgroundImageURL,
  setBackgroundImageURL,
  importBackgroundFromURL,
  importBackgroundImage,
  backgroundInputRef,
  setBackgroundImage,
  layerVisibility,
  scenarioMetadata,
}) {
  return (
    <div className="mt-3 space-y-2">
      {backgroundImage && (
        <div className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded mb-2 dark:border dark:border-green-800">
          <div className="font-medium dark:text-slate-200">
            Background Image Loaded
          </div>
          <div className="text-green-500 dark:text-green-400">
            {backgroundImage.filename || "Image"} •{" "}
            {backgroundImage.originalWidth}×{backgroundImage.originalHeight}px
          </div>
          <div className="text-green-500 dark:text-green-400">
            {backgroundImage.type.toUpperCase()} •{" "}
            {backgroundImage.source === "url" ? "URL" : "File"} • Fitted to
            ViewBox
          </div>
          {backgroundImage.source === "url" &&
            (backgroundImage.url?.startsWith("data:") ? (
              // Embedded base64 data URLs are huge; show a short label instead
              // of dumping the whole string into the panel.
              <div className="text-green-500 dark:text-green-400 text-xs mt-1">
                {backgroundImage.filename || "Eingebettetes Bild"} (Base64)
              </div>
            ) : (
              <div
                className="text-green-500 dark:text-green-400 text-xs mt-1 truncate"
                title={backgroundImage.url}
              >
                {backgroundImage.url}
              </div>
            ))}
        </div>
      )}
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-slate-300">
          Load Map Templates
        </label>
        <div className="grid grid-cols-1 gap-1">
          <button
            onClick={() => {
              const osmUrl =
                "https://tile.openstreetmap.org/15/17096/11273.png";
              importBackgroundFromURL(osmUrl);
            }}
            className="flex items-center gap-2 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-200 rounded hover:bg-green-200 dark:hover:bg-green-900/50 border border-green-300 dark:border-green-700"
          >
            <Map className="w-3 h-3" />
            OpenStreetMap (Berlin)
          </button>
          <button
            onClick={() => {
              const satelliteUrl =
                "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/15/11273/17096";
              importBackgroundFromURL(satelliteUrl);
            }}
            className="flex items-center gap-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 border border-blue-300 dark:border-blue-700"
          >
            <Map className="w-3 h-3" />
            Satellite View (Berlin)
          </button>
          <button
            onClick={() => {
              const testUrl =
                "https://stadtplatz.de/hubfs/maps/svg_master_vhp2_export_plain.png";
              setBackgroundImageURL(testUrl);
              importBackgroundFromURL(testUrl);
            }}
            className="flex items-center gap-2 px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-700"
          >
            <Building className="w-3 h-3" />
            VHP Floor Plan
          </button>
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400">
          These are sample map tiles. For custom areas, use coordinate-based
          URLs or upload your own maps.
        </div>
      </div>
      <div className="border-t dark:border-slate-700 pt-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-slate-300">
          Custom Map Tile URL
        </label>
        <input
          type="text"
          value={backgroundImageURL}
          onChange={(e) => setBackgroundImageURL(e.target.value)}
          placeholder="https://example.com/tile/{z}/{x}/{y}.png"
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded mb-1 dark:placeholder-slate-400"
        />
        <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">
          Map tile URLs use {"{z}/{x}/{y}"} format (zoom/x-coord/y-coord)
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-slate-300">
          Load from Image URL
        </label>
        <div className="flex gap-1">
          <input
            type="url"
            value={backgroundImageURL}
            onChange={(e) => setBackgroundImageURL(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded dark:placeholder-slate-400"
          />
          <button
            onClick={() => {
              importBackgroundFromURL(backgroundImageURL);
              setBackgroundImageURL("");
            }}
            disabled={!backgroundImageURL.trim()}
            className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:bg-gray-100 dark:disabled:bg-slate-700 disabled:text-gray-400 dark:disabled:text-slate-500"
          >
            Load
          </button>
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400">
          Supports JPG, PNG, GIF, WebP, SVG formats
        </div>
      </div>
      <label className="cursor-pointer block">
        <input
          type="file"
          accept="image/*,.svg,.png,.jpg,.jpeg,.gif,.webp"
          onChange={importBackgroundImage}
          className="hidden"
          ref={backgroundInputRef}
        />
        <div className="flex items-center gap-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 border border-blue-300 dark:border-blue-700">
          <FileImage className="w-3 h-3" />
          {backgroundImage ? "Replace with File" : "Upload File"}
        </div>
      </label>
      <div className="text-xs text-gray-400 dark:text-slate-500 bg-gray-50 dark:bg-slate-700/50 p-2 rounded space-y-1 dark:border dark:border-slate-600">
        <div>
          📍 Background Layer:{" "}
          {layerVisibility[LAYERS.BACKGROUND] ? "✅ Visible" : "❌ Hidden"}
        </div>
        <div>
          📐 ViewBox: {scenarioMetadata.viewBox.width}×
          {scenarioMetadata.viewBox.height}
        </div>
        {backgroundImage && (
          <>
            <div>
              🖼️ Loaded: {backgroundImage.originalWidth}×
              {backgroundImage.originalHeight}px
            </div>
            <div>
              📁 Source: {backgroundImage.source} (
              {backgroundImage.type.toUpperCase()})
            </div>
            {backgroundImage.filename && (
              <div>📄 File: {backgroundImage.filename}</div>
            )}
          </>
        )}
      </div>
      {backgroundImage && (
        <button
          onClick={() => {
            setBackgroundImage(null);
            setBackgroundImageURL("");
          }}
          className="flex items-center gap-2 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-900/50 w-full border border-red-300 dark:border-red-700"
        >
          <Trash2 className="w-3 h-3" />
          Remove Background
        </button>
      )}
    </div>
  );
}
